import React, { useState, useRef } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import toast from 'react-hot-toast';

const ImageUploadStep = ({ images = [], onImagesChange, reportId, practitionerId }) => {
  const [uploading, setUploading] = useState(false);
  const [previewImages, setPreviewImages] = useState(images);
  const fileInputRef = useRef(null);

  const handleFileSelect = async (e) => {
    const files = Array.from(e?.target?.files || []);
    
    if (files?.length === 0) return;

    // Validate file types
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    const invalidFiles = files?.filter(f => !validTypes?.includes(f?.type));
    
    if (invalidFiles?.length > 0) {
      toast?.error('Format non supporté. Utilisez JPEG, PNG ou WEBP');
      return;
    }

    // Validate file sizes (10MB max)
    const oversizedFiles = files?.filter(f => f?.size > 10485760);
    if (oversizedFiles?.length > 0) {
      toast?.error('Fichier trop volumineux. Taille maximale : 10 Mo');
      return;
    }

    setUploading(true);

    try {
      // Create preview URLs for immediate display
      const newPreviews = files?.map(file => ({
        file,
        preview: URL.createObjectURL(file),
        name: file?.name,
        size: file?.size,
        type: file?.type,
        uploading: false // Set to false since we're only creating previews, actual upload happens later
      }));

      setPreviewImages(prev => [...prev, ...newPreviews]);
      
      // Notify parent component
      onImagesChange([...previewImages, ...newPreviews]);
      
      toast?.success(`${files?.length} image(s) ajoutée(s)`);
    } catch (error) {
      console.error('Error handling files:', error);
      toast?.error('Erreur lors de l\'ajout des images');
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveImage = (index) => {
    const updatedImages = previewImages?.filter((_, i) => i !== index);
    
    // Revoke preview URL to prevent memory leaks
    if (previewImages?.[index]?.preview) {
      URL.revokeObjectURL(previewImages?.[index]?.preview);
    }
    
    setPreviewImages(updatedImages);
    onImagesChange(updatedImages);
    toast?.success('Image supprimée');
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} o`;
    if (bytes < 1048576) return `${(bytes / 1024)?.toFixed(1)} Ko`;
    return `${(bytes / 1048576)?.toFixed(1)} Mo`;
  };

  const handleUploadClick = () => {
    fileInputRef?.current?.click();
  };

  return (
    <div className="bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 p-8">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-white mb-2">
          Images et Radiographies
        </h2>
        <p className="text-gray-400">
          Ajoutez des photos cliniques ou des radiographies à votre compte rendu
        </p>
      </div>

      {/* Upload Button */}
      <div className="mb-6">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/jpg,image/png,image/webp"
          multiple
          onChange={handleFileSelect}
          disabled={uploading}
          className="hidden"
        />
        <Button
          variant="outline"
          disabled={uploading}
          onClick={handleUploadClick}
        >
          <Icon name="Upload" size={20} className="mr-2" />
          {uploading ? 'Chargement...' : 'Ajouter des images'}
        </Button>
        <p className="text-xs text-gray-500 mt-2">
          Formats acceptés : JPEG, PNG, WEBP • Taille max : 10 Mo par image
        </p>
      </div>

      {/* Image Grid */}
      {previewImages?.length > 0 && (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-white">
            Images ajoutées ({previewImages?.length})
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {previewImages?.map((img, index) => (
              <div
                key={index}
                className="relative bg-white/5 rounded-xl border border-white/10 overflow-hidden group"
              >
                {/* Image Preview */}
                <div className="aspect-video bg-gray-900 flex items-center justify-center">
                  <img
                    src={img?.preview || img?.url}
                    alt={img?.name}
                    className="w-full h-full object-cover"
                  />
                </div>

                {/* Image Info */}
                <div className="p-3">
                  <p className="text-sm text-white truncate mb-1">
                    {img?.name}
                  </p>
                  <p className="text-xs text-gray-400">
                    {formatFileSize(img?.size || img?.fileSize)}
                  </p>
                </div>

                {/* Delete Button */}
                <button
                  onClick={() => handleRemoveImage(index)}
                  className="absolute top-2 right-2 bg-red-500/90 hover:bg-red-600 text-white rounded-full p-2 opacity-0 group-hover:opacity-100 transition-opacity"
                  title="Supprimer"
                >
                  <Icon name="Trash2" size={16} />
                </button>

                {/* Uploading Overlay */}
                {img?.uploading && (
                  <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Empty State */}
      {previewImages?.length === 0 && (
        <div className="text-center py-12 border-2 border-dashed border-white/10 rounded-xl">
          <Icon name="Image" size={48} className="mx-auto text-gray-600 mb-4" />
          <p className="text-gray-400 mb-2">Aucune image ajoutée</p>
          <p className="text-sm text-gray-500">
            Les images seront affichées dans le compte rendu et le PDF
          </p>
        </div>
      )}
    </div>
  );
};

export default ImageUploadStep;
